import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Alert, CircularProgress, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { getUser, updateUser, Avatar } from '../../services/userService';
import { useAuth } from '../../hooks/useAuth';
import AvatarDisplay from '../shared/AvatarDisplay';
import AvatarSelector from '../shared/AvatarSelector';

export const StudentSettings: React.FC = () => {
  const { user: authUser } = useAuth();
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<Avatar | undefined>(undefined);
  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false);

  useEffect(() => {
    if (authUser) {
      loadProfile();
    }
  }, [authUser]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const userData = await getUser('me');
      setUserId(userData.user_id);
      setAvatar(userData.avatar);
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarSelect = async (newAvatar: Avatar) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!userId) {
        throw new Error('User not authenticated');
      }

      await updateUser(userId, { avatar: newAvatar });
      setAvatar(newAvatar);
      setSuccess('Avatar updated successfully!');

      // Dispatch event to refresh avatar in navigation
      window.dispatchEvent(new Event('avatarUpdated'));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update avatar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Student Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Customize your profile and make it your own!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Avatar Section */}
        <Typography variant="h6" gutterBottom>
          My Avatar
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box display="flex" alignItems="center" gap={3} mb={4}>
          <AvatarDisplay
            avatar={avatar}
            name={authUser?.attributes?.name || 'Student'}
            role="student"
            size="large"
            showTooltip={false}
          />
          <Box>
            <Typography variant="body1" gutterBottom>
              Choose a fun avatar that represents you!
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Your avatar will be visible to your teachers and classmates.
            </Typography>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setAvatarSelectorOpen(true)}
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Change Avatar'}
            </Button>
          </Box>
        </Box>

        {/* Future sections can be added here */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            💡 Tip: Choose an avatar that shows your personality or interests!
          </Typography>
        </Box>
      </Paper>

      {/* Avatar Selector Dialog */}
      <AvatarSelector
        open={avatarSelectorOpen}
        onClose={() => setAvatarSelectorOpen(false)}
        onSelect={handleAvatarSelect}
        currentAvatar={avatar}
        role="student"
      />
    </Box>
  );
};

export default StudentSettings;
